import * as React from 'react';

import { rgba } from 'polished';
import styled, { css } from 'styled-components';
import { getWidth } from '@components/styles';

import LockIcon from '@components/basics/icons/lock';
import Typography from '@components/basics/typography';

export type ButtonType =
  | 'primary'
  | 'secondary'
  | 'secondary-dark'
  | 'secondary-light'
  | 'secondary-transparent'
  | 'blacked'
  | 'tab'
  | 'hint'
  | 'white'
  | 'circle'
  | 'circle-grey'
  | 'circle-black'
  | 'border'
  | 'empty'
  | 'add'
  | 'alert'
  | 'alert-secondary';

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  id?: string;
  buttonType?: ButtonType;
  children?: any;
  className?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  small?: boolean;
  active?: boolean;
  cols?: number;
  color?: string;
  uppercase?: boolean;
  hasLoader?: boolean;
  locked?: boolean;
  circleSize?: number;
  height?: number;
  customContent?: React.ReactNode;
  isPillShape?: boolean;
  autoWidth?: boolean;
  customHeight?: number;
}

export const Button: React.FC<ButtonProps> = (props) => (
  <button
    form={props.form}
    id={props.id}
    className={props.className}
    type={props.type}
    onClick={!props.disabled ? props.onClick : undefined}
    onMouseDown={props.onMouseDown}
    onMouseUp={props.onMouseUp}
    disabled={props.disabled}
  >
    {!props.hasLoader && props.locked ? <StyledLockIcon /> : null}
    {props.hasLoader ? (
      props.children
    ) : props.customContent ? (
      props.customContent
    ) : (
      <ButtonText
        className={`btn-text ${props.disabled ? 'disabled' : ''} ${
          props.buttonType === 'tab' ? 'tab-btn' : ''
        }`}
        weight="700"
      >
        {props.children}
      </ButtonText>
    )}
  </button>
);

const ButtonText = styled(Typography)`
  color: ${({ theme }) => theme.colors.primary};

  &.disabled {
    color: ${({ theme }) => rgba(theme.colors.black, 0.5)};
  }

  &.tab-btn {
    color: ${({ theme }) => theme.colors.background};
  }
`;

const genericButton = css`
  background-color: ${({ theme }) => theme.colors.secondary};
`;

const tabStyleButton = css`
  background-color: ${({ theme }) => theme.colors.primary};
`;

const actionButton = () => css`
  background-color: ${({ theme }) => theme.colors.accent};
  transition: background-color
    ${({ theme }) => `${theme.transitions.duration} ${theme.transitions.type}`};

  &:hover,
  &:active {
    background-color: ${({ theme }) => theme.colors.accentHover};
  }

  span {
    color: ${({ theme }) => theme.colors.white};
  }
`;

const primaryButton = (props: ButtonProps) =>
  (!props.buttonType || props.buttonType === 'primary') && !props.disabled
    ? actionButton()
    : '';

const secondaryButton = (props: ButtonProps) =>
  props.buttonType === 'secondary' && !props.disabled
    ? css`
        ${genericButton};
        transition: background-color
            ${({ theme }) =>
              `${theme.transitions.duration} ${theme.transitions.type}`},
          color
            ${({ theme }) =>
              `${theme.transitions.duration} ${theme.transitions.type}`};

        span {
          color: ${({ theme }) => theme.colors.gray};
        }

        &:hover,
        &:active {
          background-color: ${({ theme }) => theme.colors.secondaryHover};
          span {
            color: ${({ theme }) => theme.colors.primary};
          }
        }
      `
    : '';

const secondaryDarkButton = (props: ButtonProps) =>
  props.buttonType === 'secondary-dark' && !props.disabled
    ? css`
        background-color: ${({ theme }) => theme.colors.black};
        transition: background-color
          ${({ theme }) =>
            `${theme.transitions.duration} ${theme.transitions.type}`};

        span {
          color: ${({ theme }) => theme.colors.white};
        }

        &:hover,
        &:active {
          background-color: ${({ theme }) => theme.colors.blackHover};
        }
      `
    : '';

const secondaryLightButton = (props: ButtonProps) =>
  props.buttonType === 'secondary-light' && !props.disabled
    ? css`
        ${genericButton};
        background-color: ${({ theme }) => theme.colors.secondary};
        transition: background-color
          ${({ theme }) =>
            `${theme.transitions.duration} ${theme.transitions.type}`};

        span {
          color: ${({ theme }) => theme.colors.black};
        }

        &:hover,
        &:active {
          background-color: ${({ theme }) => theme.colors.secondaryHover};
        }
      `
    : '';

const secondaryTransparentButton = (props: ButtonProps) =>
  props.buttonType === 'secondary-transparent' && !props.disabled
    ? css`
        ${genericButton};
        background-color: ${({ theme }) => rgba(theme.colors.background, 0.75)};
        transition: background-color
          ${({ theme }) =>
            `${theme.transitions.duration} ${theme.transitions.type}`};

        span {
          color: ${({ theme }) => theme.colors.primary};
        }

        &:hover,
        &:active {
          background-color: ${({ theme }) =>
            rgba(theme.colors.background, 0.5)};
        }
      `
    : '';

const tabButton = (props: ButtonProps) =>
  props.buttonType === 'tab' && !props.disabled
    ? css`
        ${tabStyleButton}
      `
    : '';

const whiteButton = (props: ButtonProps) =>
  props.buttonType === 'white' && !props.disabled
    ? css`
        background-color: ${({ theme }) => theme.colors.differentBackground};
        transition: background-color
          ${({ theme }) =>
            `${theme.transitions.duration} ${theme.transitions.type}`};

        span {
          color: ${({ theme }) => theme.colors.primary};
        }

        &:hover,
        &:active {
          background-color: ${({ theme }) => theme.colors.background};
        }
      `
    : props.buttonType === 'white' && props.disabled
    ? css`
        background-color: ${({ theme }) => theme.colors.white};
      `
    : '';

const HintButton = (props: ButtonProps) =>
  props.buttonType === 'hint'
    ? css`
        background: ${({ theme }) => theme.colors.hint};
        cursor: not-allowed;

        span {
          color: ${({ theme }) => theme.colors.hintText};
        }
      `
    : '';

const alertButton = (props: ButtonProps) =>
  props.buttonType === 'alert' && !props.disabled
    ? css`
        background-color: ${({ theme }) => rgba(theme.colors.accent, 0.25)};
        border: 1px solid ${({ theme }) => theme.colors.accent};
        transition: background-color
            ${({ theme }) =>
              `${theme.transitions.duration} ${theme.transitions.type}`},
          border-color
            ${({ theme }) =>
              `${theme.transitions.duration} ${theme.transitions.type}`},
          color
            ${({ theme }) =>
              `${theme.transitions.duration} ${theme.transitions.type}`};

        span {
          color: ${({ theme }) => theme.colors.accent};
          font-weight: 700;
        }

        &:hover,
        &:active {
          background-color: ${({ theme }) => theme.colors.accent};
          border-color: ${({ theme }) => theme.colors.accentHover};

          span {
            color: ${({ theme }) => theme.colors.white};
          }
        }
      `
    : '';

const alertSecundaryButton = (props: ButtonProps) =>
  props.buttonType === 'alert-secondary' && !props.disabled
    ? css`
        background-color: ${({ theme }) => theme.colors.secondary};
        transition: background-color
          ${({ theme }) =>
            `${theme.transitions.duration} ${theme.transitions.type}`};

        span {
          color: ${({ theme }) => theme.colors.accent};
          font-weight: 700;
          transition: color
            ${({ theme }) =>
              `${theme.transitions.duration} ${theme.transitions.type}`};
        }

        &:hover,
        &:active {
          background-color: ${({ theme }) => rgba(theme.colors.accent, 0.15)};

          span {
            color: ${({ theme }) => theme.colors.accentHover};
          }
        }
      `
    : '';

const disabledButton = (props: ButtonProps) =>
  props.disabled
    ? css`
        ${genericButton};
        cursor: not-allowed;
        pointer-events: all !important;

        span {
          color: ${({ theme }) => rgba(theme.colors.primary, 0.5)};
        }
      `
    : '';

const fullWidthButton = (props: ButtonProps) =>
  props.fullWidth
    ? css`
        width: 100%;

        ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
          width: 100%;
        }
      `
    : '';

const smallButton = (props: ButtonProps) =>
  props.small
    ? css`
        padding: 16px;
        margin: 0 auto;
        min-width: 200px;

        ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
          padding: 16px 0;
        }

        span {
          font-size: ${({ theme }) => theme.typography.h4};
          line-height: 16px;
        }
      `
    : '';

const activeButton = (props: ButtonProps) =>
  props.active
    ? css`
        ${tabStyleButton};
        transition: background-color
            ${({ theme }) =>
              `${theme.transitions.duration} ${theme.transitions.type}`},
          color
            ${({ theme }) =>
              `${theme.transitions.duration} ${theme.transitions.type}`};
        cursor: auto;

        span {
          color: ${({ theme }) => theme.colors.background};
        }

        &:hover {
          background-color: ${({ theme }) => theme.colors.primary};

          span {
            color: ${({ theme }) => theme.colors.background};
          }
        }

        &:active {
          background-color: ${({ theme }) => theme.colors.primary};
          span {
            color: ${({ theme }) => theme.colors.background};
          }
        }
      `
    : '';

const borderButton = (props: ButtonProps) =>
  props.buttonType === 'border' && !props.disabled
    ? css`
        ${whiteButton};
        background: ${({ theme }) => theme.colors.secondary};
        border: 1px solid ${({ theme }) => theme.colors.border};
        transition: background-color
            ${({ theme }) =>
              `${theme.transitions.duration} ${theme.transitions.type}`},
          color
            ${({ theme }) =>
              `${theme.transitions.duration} ${theme.transitions.type}`},
          border-color
            ${({ theme }) =>
              `${theme.transitions.duration} ${theme.transitions.type}`};

        &:hover,
        &:active {
          border-color: ${({ theme }) => theme.colors.background};
          background-color: ${({ theme }) => theme.colors.secondaryHover};

          span {
            color: ${({ theme }) => theme.colors.primary};
          }
        }
      `
    : '';

const circleButton = (props: ButtonProps) =>
  props.buttonType === 'circle' && !props.disabled
    ? css`
        ${actionButton()};
        border-radius: 50%;
        padding: 16px;
        width: 50px;
        height: 50px;

        span {
          height: 16px;
          width: 16px;
          margin: 0 auto;
        }

        & > span {
          color: ${({ theme }) => theme.colors.white};
          & > svg {
            & > * {
              fill: ${({ theme }) => theme.colors.white};
            }
          }
        }

        ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
          width: 44px;
          height: 44px;
          padding: 0;
        }
      `
    : '';

const circleButtonGrey = (props: ButtonProps) =>
  props.buttonType === 'circle-grey' && !props.disabled
    ? css`
        ${genericButton};
        border-radius: 50%;
        width: 40px;
        height: 40px;
        padding: 0;
        transition: background-color
          ${({ theme }) =>
            `${theme.transitions.duration} ${theme.transitions.type}`};

        span {
          height: 14px;
          width: 14px;
          margin: 0 auto;
        }

        &:hover {
          background-color: ${({ theme }) => theme.colors.secondary};
        }

        &:active {
          background-color: ${({ theme }) => theme.colors.secondaryHover};
        }

        ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
          width: 44px;
          height: 44px;
          padding: 0;
        }
      `
    : '';

const circleButtonBlack = (props: ButtonProps) =>
  props.buttonType === 'circle-black' && !props.disabled
    ? css`
        background-color: ${({ theme }) => theme.colors.primary};
        border-radius: 50%;
        padding: 0;
        display: flex;
        align-items: center;
        justify-content: center;

        span {
          height: 16px;
          width: 16px;
          margin: 0 auto;
        }

        & > span {
          color: ${({ theme }) => theme.colors.background};
          & > svg {
            & > * {
              fill: ${({ theme }) => theme.colors.background};
            }
          }
        }

        ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
          width: 50px;
          height: 50px;
          padding: 0;
        }
      `
    : '';

const emptyButton = (props: ButtonProps) =>
  props.buttonType === 'empty' && !props.disabled
    ? css`
        padding: 0;
        width: auto;
        border-radius: 0;
        background: transparent;

        ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
          padding: 0;
          width: auto;
        }
      `
    : '';

const addButton = (props: ButtonProps) =>
  props.buttonType === 'add' && !props.disabled
    ? css`
        padding: 0;
        width: 50px;
        height: 50px;
        background: ${({ theme }) => theme.colors.secondary};
        transition: background-color
          ${({ theme }) =>
            `${theme.transitions.duration} ${theme.transitions.type}`};

        &:hover,
        &:active {
          background-color: ${({ theme }) => theme.colors.secondaryHover};
        }

        span {
          line-height: 12px;
          font-size: 12px;
        }

        ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
          padding: 0;
          width: 50px;
          height: 50px;

          span {
            line-height: 12px;
            font-size: 12px;
          }
        }
      `
    : '';

const blackedButton = (props: ButtonProps) =>
  props.buttonType === 'blacked' && props.disabled
    ? css`
        background-color: ${({ theme }) => theme.colors.blackButton};

        span {
          color: ${({ theme }) => theme.colors.blackButtonText} !important;
        }
      `
    : null;

export default styled(Button)`
  padding: 16px ${({ theme }) => theme.general.paddingBtn};
  max-height: 48px;
  border-radius: ${({ theme }) => theme.general.borderRadius};
  cursor: pointer;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  span {
    font-weight: 700;
    line-height: 16px;
    font-size: ${({ theme }) => theme.typography.span};
  }

  ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
    width: ${(props) => getWidth(props.cols || 3)};
  }
  ${({ theme }) => `@media screen and ${theme.device.laptopL}`} {
    max-height: 60px;
    height: 60px;

    span {
      line-height: 16px;
      font-size: ${({ theme }) => theme.typography.h4};
    }

    ${(props) =>
      props.customHeight
        ? css`
            max-height: ${props.customHeight}px;
            height: ${props.customHeight}px;
          `
        : null}
  }

  ${primaryButton}
  ${secondaryButton}
  ${secondaryDarkButton}
  ${secondaryLightButton}
  ${secondaryTransparentButton}
  ${tabButton}
  ${fullWidthButton}
  ${smallButton}
  ${borderButton}
  ${activeButton}
  ${circleButton}
  ${circleButtonBlack}
  ${circleButtonGrey}
  ${alertButton}
  ${alertSecundaryButton}
  ${HintButton}
  ${emptyButton}
  ${addButton}

  ${disabledButton}
  ${blackedButton}

  ${whiteButton}

  ${(props) =>
    props.small && props.buttonType === 'add'
      ? css`
          width: 40px;
          height: 40px;

          ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
            width: 40px;
            height: 40px;
          }
        `
      : null}

  ${(props) =>
    props.uppercase
      ? css`
          span {
            text-transform: uppercase;
          }
        `
      : null}

  ${(props) =>
    props.color === 'red'
      ? css`
          span {
            color: ${({ theme }) => theme.colors.accent};
          }
        `
      : null}

  ${(props) =>
    props.circleSize
      ? css`
          ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
            width: ${props.circleSize}px;
            height: ${props.circleSize}px;
          }

          span {
            width: calc(100% - 62.5%);
            height: calc(100% - 62.5%);

            & > * {
              width: 100%;
              height: 100%;
            }
          }
        `
      : null};

  ${(props) =>
    props.locked
      ? css`
          display: flex;
          justify-content: center;
          align-items: center;
          width: 100%;
          ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
            width: 100%;
          }
        `
      : null}
  ${(props) =>
    props.height
      ? css`
          max-height: ${props.height}px;
          height: ${props.height}px;
          ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
            max-height: ${props.height}px;
            height: ${props.height}px;
          }
        `
      : null}
  ${(props) =>
    props.customContent
      ? css`
          ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
            padding-left: 20px;
            padding-right: 20px;
          }
        `
      : null}
  ${(props) =>
    props.isPillShape
      ? css`
          border-radius: 100px;
        `
      : null}
  ${(props) =>
    props.autoWidth
      ? css`
          ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
            width: auto;
          }
        `
      : null}
`;

const StyledLockIcon = styled(LockIcon)`
  margin-right: 10px;
  width: 18px;
  height: 18px;
`;
